import { useEffect, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { HOTKEY_OPTIONS } from '@/config/hotkeys';
import { usePlaybackStore } from '@/shared/state/playback';
import { useTimelineStore } from '../../stores/timeline-store';
import { useItemsStore } from '../../stores/items-store';
import { useTimelineSettingsStore } from '../../stores/timeline-settings-store';

import type { TimelineShortcutCallbacks } from '../use-timeline-shortcuts';

export function useSmbShortcuts(callbacks: TimelineShortcutCallbacks) {
  const reverseTimerRef = useRef<number | null>(null);
  const reverseRateRef = useRef(0);

  const stopReverse = () => {
    if (reverseTimerRef.current !== null) {
      window.clearInterval(reverseTimerRef.current);
      reverseTimerRef.current = null;
    }
    reverseRateRef.current = 0;
  };

  useEffect(() => {
    return () => {
      stopReverse();
    };
  }, []);

  const getEditPoints = () => {
    const items = useItemsStore.getState().items;
    const points = new Set<number>();

    for (const item of items) {
      points.add(item.from);
      points.add(item.from + item.durationInFrames);
    }

    return Array.from(points).sort((a, b) => a - b);
  };

  // J = reverse shuttle: 1x -> 2x -> 4x
  useHotkeys(
    'j',
    (event) => {
      event.preventDefault();

      const playback = usePlaybackStore.getState();
      playback.pause();
      playback.setPlaybackRate(1);

      if (callbacks.onPause) {
        callbacks.onPause();
      }

      const nextRate =
        reverseRateRef.current === 0 ? 1 :
        reverseRateRef.current === 1 ? 2 :
        4;

      reverseRateRef.current = nextRate;

      if (reverseTimerRef.current !== null) {
        window.clearInterval(reverseTimerRef.current);
      }

      const fps = useTimelineSettingsStore.getState().fps || 25;
      const intervalMs = 1000 / fps;

      reverseTimerRef.current = window.setInterval(() => {
        const state = usePlaybackStore.getState();
        const nextFrame = Math.max(
          0,
          state.currentFrame - reverseRateRef.current
        );

        state.setPreviewFrame(null);
        state.setDisplayedFrame(null);
        state.setCurrentFrame(nextFrame);

        if (nextFrame === 0) {
          stopReverse();
        }
      }, intervalMs);
    },
    HOTKEY_OPTIONS,
    [callbacks]
  );

  // K = stop
  useHotkeys(
    'k',
    (event) => {
      event.preventDefault();

      stopReverse();

      const playback = usePlaybackStore.getState();
      playback.pause();
      playback.setPlaybackRate(1);

      if (callbacks.onPause) {
        callbacks.onPause();
      }
    },
    HOTKEY_OPTIONS,
    [callbacks]
  );

  // L = forward shuttle: 1x -> 2x -> 4x
  useHotkeys(
    'l',
    (event) => {
      event.preventDefault();

      stopReverse();

      const playback = usePlaybackStore.getState();

      let nextRate = 1;

      if (playback.isPlaying) {
        if (playback.playbackRate < 1.5) {
          nextRate = 2;
        } else {
          nextRate = 4;
        }
      }

      playback.setPlaybackRate(nextRate);
      playback.play();

      if (callbacks.onPlay) {
        callbacks.onPlay();
      }
    },
    HOTKEY_OPTIONS,
    [callbacks]
  );

  // A = previous edit point
  useHotkeys(
    'a',
    (event) => {
      event.preventDefault();

      stopReverse();

      const playback = usePlaybackStore.getState();
      const points = getEditPoints();

      for (let i = points.length - 1; i >= 0; i--) {
        const point = points[i];
        if (point < playback.currentFrame) {
          playback.setPreviewFrame(null);
          playback.setDisplayedFrame(null);
          playback.setCurrentFrame(point);
          break;
        }
      }
    },
    HOTKEY_OPTIONS,
    []
  );

  // D = next edit point
  useHotkeys(
    'd',
    (event) => {
      event.preventDefault();

      stopReverse();

      const playback = usePlaybackStore.getState();
      const points = getEditPoints();
      const nextPoint = points.find((point) => point > playback.currentFrame);

      if (nextPoint !== undefined) {
        playback.setPreviewFrame(null);
        playback.setDisplayedFrame(null);
        playback.setCurrentFrame(nextPoint);
      }
    },
    HOTKEY_OPTIONS,
    []
  );

  // B = trim everything LEFT of playhead inside clips under playhead
  useHotkeys(
    'b',
    (event) => {
      event.preventDefault();

      stopReverse();

      const currentFrame = usePlaybackStore.getState().currentFrame;
      const timeline = useTimelineStore.getState();

      const overlappingItems = timeline.items.filter((item) => {
        const end = item.from + item.durationInFrames;
        return currentFrame > item.from && currentFrame < end;
      });

      for (const item of overlappingItems) {
        const trimAmount = currentFrame - item.from;
        timeline.trimItemStart(item.id, trimAmount);
      }
    },
    HOTKEY_OPTIONS,
    []
  );

  // N = trim everything RIGHT of playhead inside clips under playhead
  useHotkeys(
    'n',
    (event) => {
      event.preventDefault();

      stopReverse();

      const currentFrame = usePlaybackStore.getState().currentFrame;
      const timeline = useTimelineStore.getState();

      const overlappingItems = timeline.items.filter((item) => {
        const end = item.from + item.durationInFrames;
        return currentFrame > item.from && currentFrame < end;
      });

      for (const item of overlappingItems) {
        const itemEnd = item.from + item.durationInFrames;
        const trimAmount = -(itemEnd - currentFrame);
        timeline.trimItemEnd(item.id, trimAmount);
      }
    },
    HOTKEY_OPTIONS,
    []
  );
}
